import { atom } from "jotai";
import { handleErrors } from "../logic/handleErrors";
import { Workbook, WorkbookVersion } from "../core/Workbook";
import {
  workbookSwitchToTab,
  workbookDeleteViewAndDataSet,
  workbookAddView,
  workbookDuplicateView,
} from "../logic/workbooks";
import { newWorkbook } from "../logic/newWorkbook";
import { isWorkbookDirtyAtom, workbookAtom } from "./WorkbookAtoms";
import { upgradeWorkbook } from "../core/legacy/WorkbookVersionConverters";
import { viewOrderAtom } from "./WorkbookFocusAtoms";

export const addViewAtom = atom(null, (get, set, viewType) => {
  const oldWorkbook = get(workbookAtom);
  const workbook = { ...oldWorkbook }; // Copy defensively to avoid render misses
  const newWorkbook = workbookAddView(
    workbook,
    viewType as string | null | undefined
  );
  set(workbookAtom, newWorkbook);
});

export const switchToViewAtom = atom(null, (get, set, targetViewId: number) => {
  const workbook = get(workbookAtom);
  const newWorkbook = workbookSwitchToTab({ ...workbook }, targetViewId);
  set(workbookAtom, newWorkbook);
});

export const moveTabAtom = atom(
  null,
  (get, set, params: { from: number; to: number }) => {
    const viewOrder = get(viewOrderAtom);
    let newViewOrder = [...viewOrder];
    newViewOrder.splice(params.to, 0, newViewOrder.splice(params.from, 1)[0]);
    console.log("Updateing vieworder from", viewOrder, "to", newViewOrder);
    set(viewOrderAtom, newViewOrder);
  }
);

export const newWorkbookAtom = atom(null, (get, set, data) => {
  set(workbookAtom, newWorkbook());
  set(isWorkbookDirtyAtom, false);
  set(inDraftModeAtom, true);
});
export const loadWorkbookAtom = atom(null, (get, set, data) => {
  const option = {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "POST",
    body: JSON.stringify({ id: data }),
  };
  fetch("/workbook/load", option)
    .then((res) => {
      if (res.ok) {
        return res.json();
      } else {
        return null;
      }
    })
    .then((workbook) => {
      if (workbook) {
        if (workbook.version !== WorkbookVersion) {
          // TODO: Ask the user if its okay to convert format
          workbook = upgradeWorkbook(workbook, WorkbookVersion);
        }
        set(workbookAtom, workbook);
        set(isWorkbookDirtyAtom, false);
        set(inDraftModeAtom, false);
      } else {
        throw new Error("Couldn't load workbook");
      }
    })
    .catch(handleErrors);
});
export const saveInProgressAtom = atom(false); // Use to prevent queue of save requests from autosave
export const saveWorkbookAtom = atom(null, (get, set, data) => {
  set(saveInProgressAtom, true);
  set(inDraftModeAtom, false);
  const workbook = get(workbookAtom);

  const option = {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "POST",
    body: JSON.stringify(workbook),
  };
  fetch("/workbook/save", option)
    .then((res) => {
      if (res.ok) {
        set(isWorkbookDirtyAtom, false);
        return true;
      } else {
        return false;
      }
    })
    .catch(handleErrors)
    .finally(() => {
      set(saveInProgressAtom, false);
    });
});
// Some atoms to manage when we can and can't autosave
// Logic is:
//    don't save if a save is already in progress, and don't save initial edits on an untitled workbook
export const inDraftModeAtom = atom(true); // Turn off when workbook name is set, or workbook is saved
export const canAutosaveAtom = atom((get) => {
  const saveInProgress = get(saveInProgressAtom);
  const inDraftMode = get(inDraftModeAtom);
  return !saveInProgress && !inDraftMode;
});
//TODO: export for testing only. Work out how to mock without exporting
export const workbookNames = atom<null | [string, string][]>(null);
const fetchWorkbookNames = (): Promise<[string, string][]> => {
  return fetch("/workbook/list").then((res) => {
    if (res.ok) {
      return res.json();
    } else {
      return [];
    }
  });
};
export const listWorkbooksAtom = atom(
  (get) => get(workbookNames),
  (get, set, data) => {
    if (data === "CLEAR") {
      set(workbookNames, null);
    } else {
      fetchWorkbookNames()
        .then((names) => {
          set(workbookNames, names);
        })
        .catch(handleErrors);
    }
  }
);

export const deleteViewAtom = atom(
  null,
  (get, set, { viewId }: { viewId: string }) => {
    const workbook = get(workbookAtom);
    const newWorkbook = workbookDeleteViewAndDataSet(workbook, viewId);
    set(workbookAtom, newWorkbook);
  }
);

export const duplicateViewAtom = atom(
  null,
  (get, set, { viewId }: { viewId: string }) => {
    const workbook = get(workbookAtom);
    const newWorkbook: Workbook = workbookDuplicateView(
      { ...workbook },
      viewId
    );
    set(workbookAtom, newWorkbook);
  }
);
